/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { showErrorThunk } from '@spike/notifications-action';
import {
    EmailEditorConfig,
    EmailTemplateDetail,
    EmailTemplateSaveInput,
    AudienceCustomersPage,
    AudienceCustomersQuery,
    AudienceEstimate,
    AudienceGroupCounters,
    AudienceSelection,
    EmailMarketingKpis,
    EmailMarketingKpisFilters,
    EmailMarketingLocation,
    EmailMarketingSender,
    EmailMarketingTemplateOption,
    OrganizationCampaign,
    OrganizationCampaignInput
} from 'model/emailMarketing';
import { extractErrorMessage } from 'utils/ApiErrorUtils';
import {
    EMAIL_MARKETING_CAMPAIGN_DELETE_SUCCESS,
    EMAIL_MARKETING_CAMPAIGN_FETCH_START,
    EMAIL_MARKETING_CAMPAIGN_FETCH_SUCCESS,
    EMAIL_MARKETING_CAMPAIGN_SAVE_START,
    EMAIL_MARKETING_CAMPAIGN_SAVE_SUCCESS,
    EMAIL_MARKETING_CAMPAIGNS_FETCH_START,
    EMAIL_MARKETING_CAMPAIGNS_FETCH_SUCCESS,
    EMAIL_MARKETING_CLEAR_ERROR,
    EMAIL_MARKETING_ERROR,
    EMAIL_MARKETING_KPIS_FETCH_START,
    EMAIL_MARKETING_KPIS_FETCH_SUCCESS,
    EMAIL_MARKETING_LOCATIONS_FETCH_START,
    EMAIL_MARKETING_LOCATIONS_FETCH_SUCCESS,
    EMAIL_MARKETING_SENDER_SAVE_SUCCESS,
    EMAIL_MARKETING_SENDERS_FETCH_START,
    EMAIL_MARKETING_SENDERS_FETCH_SUCCESS,
    EMAIL_MARKETING_TEMPLATES_FETCH_START,
    EMAIL_MARKETING_TEMPLATES_FETCH_SUCCESS,
    EmailMarketingAction
} from './EmailMarketingActionsTypes';
import {
    convertToEditorConfig,
    convertToTemplateDetail,
    toTemplateSaveDto,
    convertToAudienceEstimate,
    convertToCustomersPage,
    convertToGroupCounters,
    convertToEmailMarketingLocation,
    convertToKpis,
    convertToOrganizationCampaign,
    convertToOrganizationCampaignInputDto,
    convertToSender,
    convertToTemplateOption,
    toAudienceQueryDto,
    toKpisQueryDto
} from './EmailMarketingConverter';
import {
    EmailEditorConfigDto,
    EmailTemplateDetailDto,
    AudienceCustomersDto,
    AudienceEstimateDto,
    AudienceGroupCountersDto,
    EmailMarketingKpisDto,
    EmailMarketingLocationsResponseDto,
    EmailMarketingSenderDto,
    EmailMarketingSendersResponseDto,
    EmailMarketingTemplatesResponseDto,
    OrganizationCampaignResponseDto,
    OrganizationCampaignsResponseDto
} from './EmailMarketingDtos';

type EmailMarketingThunk<T> = ThunkAction<Promise<T>, RootState, null, EmailMarketingAction>;

const campaignsUrl = (organizationId: number): string => `/organizations/${organizationId}/organization_campaigns`;
const campaignUrl = (organizationId: number, campaignId: number): string =>
    `${campaignsUrl(organizationId)}/${campaignId}`;
const sendersUrl = (organizationId: number): string => `/organizations/${organizationId}/email_marketing_senders`;
const senderUrl = (organizationId: number): string => `/organizations/${organizationId}/email_marketing_sender`;
const kpisUrl = (organizationId: number): string => `/organizations/${organizationId}/email_campaigns/kpis`;

const error = (err: string): EmailMarketingAction => ({
    type: EMAIL_MARKETING_ERROR,
    payload: { error: err }
});

export const clearEmailMarketingError = (): EmailMarketingAction => ({
    type: EMAIL_MARKETING_CLEAR_ERROR
});

// Stores the error, shows it as a toast (422s carry the reason in `error`) and rethrows it.
const handleError = (dispatch: any, apiError: unknown): never => {
    const message = extractErrorMessage(apiError);
    dispatch(error(message));
    dispatch(showErrorThunk(message) as any);
    throw apiError;
};

export const fetchEmailMarketingLocationsThunk = (
    organizationId: number
): EmailMarketingThunk<Array<EmailMarketingLocation>> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_LOCATIONS_FETCH_START });

        try {
            const { data }: AxiosResponse<EmailMarketingLocationsResponseDto> = await ApiClient.get(
                `${campaignsUrl(organizationId)}/locations`
            );
            const locations = data.locations.map(convertToEmailMarketingLocation);

            dispatch({ type: EMAIL_MARKETING_LOCATIONS_FETCH_SUCCESS, payload: { locations } });
            return locations;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

export const fetchEmailMarketingTemplatesThunk = (
    organizationId: number
): EmailMarketingThunk<Array<EmailMarketingTemplateOption>> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_TEMPLATES_FETCH_START });

        try {
            const { data }: AxiosResponse<EmailMarketingTemplatesResponseDto> = await ApiClient.get(
                `${campaignsUrl(organizationId)}/templates`
            );
            const templates = data.email_templates.map(convertToTemplateOption);

            dispatch({ type: EMAIL_MARKETING_TEMPLATES_FETCH_SUCCESS, payload: { templates } });
            return templates;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

export const fetchOrganizationCampaignsThunk = (
    organizationId: number
): EmailMarketingThunk<Array<OrganizationCampaign>> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_CAMPAIGNS_FETCH_START });

        try {
            const { data }: AxiosResponse<OrganizationCampaignsResponseDto> = await ApiClient.get(
                campaignsUrl(organizationId)
            );
            const campaigns = data.organization_campaigns.map(convertToOrganizationCampaign);

            dispatch({ type: EMAIL_MARKETING_CAMPAIGNS_FETCH_SUCCESS, payload: { campaigns } });
            return campaigns;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

export const fetchOrganizationCampaignThunk = (
    organizationId: number,
    campaignId: number
): EmailMarketingThunk<OrganizationCampaign> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_CAMPAIGN_FETCH_START });

        try {
            const { data }: AxiosResponse<OrganizationCampaignResponseDto> = await ApiClient.get(
                campaignUrl(organizationId, campaignId)
            );
            const campaign = convertToOrganizationCampaign(data.organization_campaign);

            dispatch({ type: EMAIL_MARKETING_CAMPAIGN_FETCH_SUCCESS, payload: { campaign } });
            return campaign;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

// Create, update, duplicate and send share the same flow: the API returns the saved campaign.
const saveCampaignThunk = (
    request: () => Promise<AxiosResponse<OrganizationCampaignResponseDto>>
): EmailMarketingThunk<OrganizationCampaign> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_CAMPAIGN_SAVE_START });

        try {
            const { data } = await request();
            const campaign = convertToOrganizationCampaign(data.organization_campaign);

            dispatch({ type: EMAIL_MARKETING_CAMPAIGN_SAVE_SUCCESS, payload: { campaign } });
            return campaign;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

export const createOrganizationCampaignThunk = (organizationId: number, input: OrganizationCampaignInput) =>
    saveCampaignThunk(() => ApiClient.post(campaignsUrl(organizationId), convertToOrganizationCampaignInputDto(input)));

// Only drafts can be edited.
export const updateOrganizationCampaignThunk = (
    organizationId: number,
    campaignId: number,
    input: OrganizationCampaignInput
) =>
    saveCampaignThunk(() =>
        ApiClient.patch(campaignUrl(organizationId, campaignId), convertToOrganizationCampaignInputDto(input))
    );

// Creates a new draft named "<name> [Copy N]".
export const duplicateOrganizationCampaignThunk = (organizationId: number, campaignId: number) =>
    saveCampaignThunk(() => ApiClient.post(`${campaignUrl(organizationId, campaignId)}/duplicate`));

// Sends the draft from every selected location.
export const sendOrganizationCampaignThunk = (organizationId: number, campaignId: number) =>
    saveCampaignThunk(() => ApiClient.post(`${campaignUrl(organizationId, campaignId)}/send`));

// Only drafts can be deleted.
export const deleteOrganizationCampaignThunk = (
    organizationId: number,
    campaignId: number
): EmailMarketingThunk<void> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_CAMPAIGN_SAVE_START });

        try {
            await ApiClient.delete(campaignUrl(organizationId, campaignId));
            dispatch({ type: EMAIL_MARKETING_CAMPAIGN_DELETE_SUCCESS, payload: { campaignId } });
        } catch (apiError) {
            handleError(dispatch, apiError);
        }
    };
};

export const fetchEmailMarketingKpisThunk = (
    organizationId: number,
    filters: EmailMarketingKpisFilters = {}
): EmailMarketingThunk<EmailMarketingKpis> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_KPIS_FETCH_START });

        try {
            const { data }: AxiosResponse<EmailMarketingKpisDto> = await ApiClient.get(kpisUrl(organizationId), {
                params: toKpisQueryDto(filters)
            });
            const kpis = convertToKpis(data);

            dispatch({ type: EMAIL_MARKETING_KPIS_FETCH_SUCCESS, payload: { kpis } });
            return kpis;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

export const fetchEmailMarketingSendersThunk = (
    organizationId: number
): EmailMarketingThunk<Array<EmailMarketingSender>> => {
    return async dispatch => {
        dispatch({ type: EMAIL_MARKETING_SENDERS_FETCH_START });

        try {
            const { data }: AxiosResponse<EmailMarketingSendersResponseDto> = await ApiClient.get(
                sendersUrl(organizationId)
            );
            const senders = data.senders.map(convertToSender);

            dispatch({ type: EMAIL_MARKETING_SENDERS_FETCH_SUCCESS, payload: { senders } });
            return senders;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

// Shared by save, disable and refresh: the API returns the location's sender.
const senderRequestThunk = (
    request: () => Promise<AxiosResponse<EmailMarketingSenderDto>>
): EmailMarketingThunk<EmailMarketingSender> => {
    return async dispatch => {
        try {
            const { data } = await request();
            const sender = convertToSender(data);

            dispatch({ type: EMAIL_MARKETING_SENDER_SAVE_SUCCESS, payload: { sender } });
            return sender;
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

// Sets the sender email and sends the verification link. Saving the same unverified email sends it again.
export const saveEmailMarketingSenderThunk = (organizationId: number, locationId: number, senderEmail: string) =>
    senderRequestThunk(() =>
        ApiClient.patch(
            senderUrl(organizationId),
            { sender_email: senderEmail },
            { params: { marketplace_id: locationId } }
        )
    );

export const disableEmailMarketingSenderThunk = (organizationId: number, locationId: number) =>
    senderRequestThunk(() =>
        ApiClient.patch(senderUrl(organizationId), { enabled: false }, { params: { marketplace_id: locationId } })
    );

// Checks the verification status with the email provider.
export const refreshEmailMarketingSenderThunk = (organizationId: number, locationId: number) =>
    senderRequestThunk(() =>
        ApiClient.post(`${senderUrl(organizationId)}/refresh`, null, { params: { marketplace_id: locationId } })
    );

// Reads that aren't kept in the store (audience counts, editor data): each screen asks for what it
// shows. Errors still go through the toast.
const getQueryThunk = <Dto, T>(
    path: string,
    params: AxiosRequestConfig['params'],
    convert: (dto: Dto) => T
): EmailMarketingThunk<T> => {
    return async dispatch => {
        try {
            const { data }: AxiosResponse<Dto> = await ApiClient.get(path, { params });
            return convert(data);
        } catch (apiError) {
            return handleError(dispatch, apiError);
        }
    };
};

export const fetchAudienceEstimateThunk = (organizationId: number, selection: AudienceSelection) =>
    getQueryThunk<AudienceEstimateDto, AudienceEstimate>(
        `${campaignsUrl(organizationId)}/audience_estimate`,
        toAudienceQueryDto(selection),
        convertToAudienceEstimate
    );

export const fetchAudienceGroupCountersThunk = (organizationId: number, selection: AudienceSelection) =>
    getQueryThunk<AudienceGroupCountersDto, AudienceGroupCounters>(
        `${campaignsUrl(organizationId)}/customer_group_counters`,
        toAudienceQueryDto(selection),
        convertToGroupCounters
    );

export const fetchAudienceCustomersThunk = (
    organizationId: number,
    selection: AudienceSelection,
    query: AudienceCustomersQuery
) =>
    getQueryThunk<AudienceCustomersDto, AudienceCustomersPage>(
        `${campaignsUrl(organizationId)}/customers`,
        toAudienceQueryDto(selection, query),
        convertToCustomersPage
    );

const templatesUrl = (organizationId: number): string => `/organizations/${organizationId}/email_templates`;

// Template actions (CUD-5781) answer with the changed templates but without html, so the picker list
// (which has html for the thumbnails) is reloaded after each one.
const templateActionThunk = (organizationId: number, request: () => Promise<unknown>): EmailMarketingThunk<void> => {
    return async dispatch => {
        try {
            await request();
        } catch (apiError) {
            handleError(dispatch, apiError);
        }
        await dispatch(fetchEmailMarketingTemplatesThunk(organizationId));
    };
};

// Copy in the same location, named "<name> [Copy N]".
export const duplicateEmailTemplateThunk = (organizationId: number, templateId: number) =>
    templateActionThunk(organizationId, () =>
        ApiClient.post(`${templatesUrl(organizationId)}/${templateId}/duplicate`)
    );

// One independent copy per location; also how a gallery template is added to locations.
export const copyEmailTemplateThunk = (organizationId: number, templateId: number, locationIds: Array<number>) =>
    templateActionThunk(organizationId, () =>
        ApiClient.post(`${templatesUrl(organizationId)}/${templateId}/copy`, { marketplace_ids: locationIds })
    );

// Fails with a readable message when a campaign still uses the template.
export const deleteEmailTemplateThunk = (organizationId: number, templateId: number) =>
    templateActionThunk(organizationId, () => ApiClient.delete(`${templatesUrl(organizationId)}/${templateId}`));

// Template editor (CUD-5781). The editor keeps its own state, so these return data instead of storing it.
export const fetchEmailEditorConfigThunk = (organizationId: number) =>
    getQueryThunk<EmailEditorConfigDto, EmailEditorConfig>(
        `/organizations/${organizationId}/email_editor/config`,
        {},
        convertToEditorConfig
    );

export const fetchEmailTemplateDetailThunk = (organizationId: number, templateId: number) =>
    getQueryThunk<EmailTemplateDetailDto, EmailTemplateDetail>(
        `${templatesUrl(organizationId)}/${templateId}`,
        {},
        convertToTemplateDetail
    );

// Creates the template in `locationId`, or saves an existing one; then reloads the template list.
export const saveEmailTemplateThunk = (
    organizationId: number,
    input: EmailTemplateSaveInput,
    target: { templateId: number } | { locationId: number }
): EmailMarketingThunk<void> =>
    templateActionThunk(organizationId, () =>
        'templateId' in target
            ? ApiClient.patch(`${templatesUrl(organizationId)}/${target.templateId}`, toTemplateSaveDto(input))
            : ApiClient.post(templatesUrl(organizationId), toTemplateSaveDto(input), {
                  params: { marketplace_id: target.locationId }
              })
    );
